package songwritersnotebook.app.components

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.AutoAwesome
import androidx.compose.material.icons.filled.Check
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.Settings
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import songwritersnotebook.app.ui.theme.SuccessGreen
import androidx.compose.material.icons.filled.DeleteSweep
import songwritersnotebook.app.viewmodels.EditorViewModel
import kotlinx.coroutines.launch
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.foundation.gestures.detectDragGestures
import androidx.compose.foundation.gestures.scrollBy

@Composable
fun HarmonyAiTabContent(
    viewModel: EditorViewModel
) {
    val uiState by viewModel.uiState.collectAsState()
    var showHarmonySettings by remember { mutableStateOf(false) }
    
    if (uiState.showClearChatDialog) {
        DeleteConfirmationDialog(
            title = "Clear Chat History?",
            text = "Are you sure you want to clear all chat messages for this song? This cannot be undone.",
            onConfirm = { viewModel.clearHarmonyChatHistory() },
            onDismiss = { viewModel.onClearChatDismiss() }
        )
    }
    
    Column(
        modifier = Modifier
            .fillMaxSize()
            .padding(top = 8.dp),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        // 1. Tab Header Area
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 16.dp, vertical = 8.dp)
                .background(
                    color = MaterialTheme.colorScheme.primaryContainer.copy(alpha = 0.3f),
                    shape = RoundedCornerShape(12.dp)
                )
                .padding(12.dp),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Box(
                    modifier = Modifier
                        .size(40.dp)
                        .background(MaterialTheme.colorScheme.primary.copy(alpha = 0.1f), CircleShape),
                    contentAlignment = Alignment.Center
                ) {
                    Icon(
                        Icons.Default.AutoAwesome,
                        contentDescription = null,
                        tint = MaterialTheme.colorScheme.primary,
                        modifier = Modifier.size(24.dp)
                    )
                }
                Spacer(Modifier.width(12.dp))
                Column {
                    Text(
                        "Harmony AI",
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.ExtraBold,
                        color = MaterialTheme.colorScheme.onSurface
                    )
                    Text(
                        "Your songwriting assistant",
                        style = MaterialTheme.typography.bodySmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
            }
            
            Box {
                IconButton(onClick = { showHarmonySettings = true }, modifier = Modifier.size(32.dp)) {
                    Icon(
                        Icons.Default.Settings,
                        contentDescription = "Harmony Settings",
                        tint = MaterialTheme.colorScheme.onSurfaceVariant,
                        modifier = Modifier.size(20.dp)
                    )
                }
                
                DropdownMenu(
                    expanded = showHarmonySettings,
                    onDismissRequest = { showHarmonySettings = false }
                ) {
                    DropdownMenuItem(
                        text = { 
                            Row(verticalAlignment = Alignment.CenterVertically) {
                                Checkbox(
                                    checked = uiState.editorSettings.harmonyVoiceEnabled,
                                    onCheckedChange = { viewModel.toggleHarmonyVoiceEnabled() }
                                )
                                Text("Harmony speaks aloud", fontSize = 14.sp)
                            }
                        },
                        onClick = { viewModel.toggleHarmonyVoiceEnabled() }
                    )
                    
                    DropdownMenuItem(
                        text = { 
                            Row(verticalAlignment = Alignment.CenterVertically) {
                                Checkbox(
                                    checked = uiState.editorSettings.harmonyConfirmationRequired,
                                    onCheckedChange = { viewModel.toggleHarmonyConfirmation() }
                                )
                                Text("Require confirmation for actions", fontSize = 14.sp)
                            }
                        },
                        onClick = { viewModel.toggleHarmonyConfirmation() }
                    )

                    HorizontalDivider(modifier = Modifier.padding(vertical = 4.dp))

                    DropdownMenuItem(
                        text = { 
                            Row(verticalAlignment = Alignment.CenterVertically) {
                                Icon(
                                    Icons.Default.DeleteSweep, 
                                    contentDescription = null, 
                                    tint = MaterialTheme.colorScheme.error,
                                    modifier = Modifier.size(20.dp)
                                )
                                Spacer(Modifier.width(12.dp))
                                Text("Clear chat history", color = MaterialTheme.colorScheme.error, fontSize = 14.sp)
                            }
                        },
                        onClick = { 
                            showHarmonySettings = false
                            viewModel.onClearChatRequest() 
                        }
                    )
                }
            }
        }

        // 2. Chat History Area
        val listState = rememberLazyListState()
        
        // Auto-scroll to bottom logic
        LaunchedEffect(uiState.harmonyChatHistory.size, uiState.isHarmonyThinking, uiState.isHarmonyListening) {
            val totalItems = uiState.harmonyChatHistory.size + 
                (if (uiState.isHarmonyThinking) 1 else 0) + 
                (if (uiState.isHarmonyListening) 1 else 0) +
                (if (uiState.harmonyPendingActions.isNotEmpty()) 1 else 0)
            
            if (totalItems > 0 && listState.layoutInfo.visibleItemsInfo.lastOrNull()?.index == totalItems - 2) {
                listState.animateScrollToItem(totalItems - 1)
            } else if (totalItems > 0 && uiState.harmonyChatHistory.size <= 50) {
                // Force scroll on initial load
                listState.scrollToItem(totalItems - 1)
            }
        }
        
        // Pagination logic (load more when scrolling to top)
        LaunchedEffect(listState) {
            androidx.compose.runtime.snapshotFlow { listState.firstVisibleItemIndex }
                .collect { index ->
                    if (index == 0 && uiState.harmonyChatHistory.size >= 50) {
                        viewModel.loadMoreHarmonyChatHistory()
                    }
                }
        }

        Box(
            modifier = Modifier
                .fillMaxWidth()
                .weight(1f)
        ) {
            LazyColumn(
                state = listState,
                modifier = Modifier.fillMaxSize(),
                contentPadding = PaddingValues(16.dp),
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                // Chat Bubbles
                items(uiState.harmonyChatHistory) { message ->
                    HarmonyChatBubble(message.text, message.isUser)
                }
                
                // Thinking State
                if (uiState.isHarmonyThinking) {
                    item {
                        HarmonyChatBubble(text = "Thinking...", isUser = false, isLoading = true)
                    }
                }
                
                // Listening State
                if (uiState.isHarmonyListening) {
                    item {
                        HarmonyChatBubble(
                            text = uiState.harmonyTranscript.ifBlank { "Listening..." },
                            isUser = true,
                            isTranscribing = true
                        )
                    }
                }

                // Pending Actions
                if (uiState.harmonyPendingActions.isNotEmpty()) {
                    item {
                        HarmonyActionCard(
                            actions = uiState.harmonyPendingActions,
                            onApprove = { viewModel.onHarmonyActionApprove(it) },
                            onReject = { viewModel.onHarmonyActionReject(it) },
                            onApproveAll = { viewModel.onHarmonyActionApproveAll() }
                        )
                    }
                }
            }
            
            VerticalScrollbar(
                listState = listState,
                modifier = Modifier.align(Alignment.CenterEnd).padding(end = 4.dp)
            )
        }
    }
}

@Composable
fun VerticalScrollbar(
    listState: androidx.compose.foundation.lazy.LazyListState,
    modifier: Modifier = Modifier,
    color: Color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.3f),
    width: androidx.compose.ui.unit.Dp = 6.dp
) {
    val coroutineScope = rememberCoroutineScope()
    val layoutInfo = listState.layoutInfo
    val totalItems = layoutInfo.totalItemsCount
    if (totalItems == 0) return

    val visibleItemsInfo = layoutInfo.visibleItemsInfo
    if (visibleItemsInfo.isEmpty()) return

    val firstVisible = visibleItemsInfo.first().index
    val lastVisible = visibleItemsInfo.last().index
    val visibleCount = lastVisible - firstVisible + 1

    if (visibleCount >= totalItems) return

    val scrollProportion = firstVisible.toFloat() / (totalItems - visibleCount).coerceAtLeast(1).toFloat()

    BoxWithConstraints(modifier = modifier.fillMaxHeight().width(width).padding(vertical = 16.dp)) {
        val totalHeight = maxHeight
        val thumbHeight = 40.dp
        
        val maxOffsetY = totalHeight - thumbHeight
        val thumbOffsetY = (maxOffsetY * scrollProportion).coerceIn(0.dp, maxOffsetY)

        Box(
            modifier = Modifier
                .offset(y = thumbOffsetY)
                .height(thumbHeight)
                .fillMaxWidth()
                .background(color, RoundedCornerShape(percent = 50))
                .pointerInput(Unit) {
                    detectDragGestures { change, dragAmount ->
                        change.consume()
                        coroutineScope.launch {
                            val multiplier = totalItems.toFloat() / visibleCount.coerceAtLeast(1).toFloat()
                            listState.scrollBy(dragAmount.y * multiplier)
                        }
                    }
                }
        )
    }
}

@Composable
fun HarmonyChatBubble(
    text: String,
    isUser: Boolean,
    isLoading: Boolean = false,
    isTranscribing: Boolean = false
) {
    val alignment = if (isUser) Alignment.CenterEnd else Alignment.CenterStart
    val backgroundColor = if (isUser) MaterialTheme.colorScheme.primaryContainer else MaterialTheme.colorScheme.surfaceVariant
    val textColor = if (isUser) MaterialTheme.colorScheme.onPrimaryContainer else MaterialTheme.colorScheme.onSurfaceVariant
    val shape = if (isUser) {
        RoundedCornerShape(topStart = 16.dp, topEnd = 16.dp, bottomStart = 16.dp, bottomEnd = 4.dp)
    } else {
        RoundedCornerShape(topStart = 16.dp, topEnd = 16.dp, bottomStart = 4.dp, bottomEnd = 16.dp)
    }

    Box(modifier = Modifier.fillMaxWidth(), contentAlignment = alignment) {
        Surface(
            color = backgroundColor,
            shape = shape,
            tonalElevation = 1.dp,
            modifier = Modifier.widthIn(max = 280.dp)
        ) {
            Row(
                modifier = Modifier.padding(horizontal = 12.dp, vertical = 8.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                if (isLoading) {
                    CircularProgressIndicator(modifier = Modifier.size(14.dp), strokeWidth = 2.dp)
                }
                Text(
                    text = text,
                    style = MaterialTheme.typography.bodyMedium,
                    color = textColor,
                    fontStyle = if (isTranscribing) androidx.compose.ui.text.font.FontStyle.Italic else androidx.compose.ui.text.font.FontStyle.Normal
                )
            }
        }
    }
}

@Composable
fun HarmonyActionCard(
    actions: List<songwritersnotebook.app.viewmodels.HarmonyPendingAction>,
    onApprove: (String) -> Unit,
    onReject: (String) -> Unit,
    onApproveAll: () -> Unit
) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(
            containerColor = MaterialTheme.colorScheme.secondaryContainer.copy(alpha = 0.4f)
        ),
        shape = RoundedCornerShape(16.dp)
    ) {
        Column(modifier = Modifier.padding(12.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                Icon(Icons.Default.AutoAwesome, null, modifier = Modifier.size(16.dp), tint = MaterialTheme.colorScheme.primary)
                Text("Harmony suggests:", style = MaterialTheme.typography.labelLarge, fontWeight = FontWeight.Bold)
            }
            
            actions.forEach { action ->
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .background(MaterialTheme.colorScheme.surface.copy(alpha = 0.5f), RoundedCornerShape(8.dp))
                        .padding(8.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(8.dp)
                ) {
                    Text(
                        text = action.toolName.replace("_", " ").replaceFirstChar { it.uppercase() },
                        style = MaterialTheme.typography.bodySmall,
                        modifier = Modifier.weight(1f)
                    )
                    IconButton(onClick = { onReject(action.id) }, modifier = Modifier.size(28.dp)) {
                        Icon(Icons.Default.Close, null, tint = MaterialTheme.colorScheme.error, modifier = Modifier.size(18.dp))
                    }
                    IconButton(onClick = { onApprove(action.id) }, modifier = Modifier.size(28.dp)) {
                        Icon(Icons.Default.Check, null, tint = SuccessGreen, modifier = Modifier.size(18.dp))
                    }
                }
            }
            
            if (actions.size > 1) {
                Button(
                    onClick = onApproveAll,
                    modifier = Modifier.fillMaxWidth(),
                    contentPadding = PaddingValues(0.dp)
                ) {
                    Text("Approve All", fontSize = 12.sp)
                }
            }
        }
    }
}
